/*!
 * Signature Legacy — Product Interview
 * - Teaser: autoplay muted/loop, "Tap to play" enables sound
 * - Custom controls for BOTH teaser + full film: Play/Pause, Stop, Mute, Volume
 * - Full film modal: scroll-lock + ESC close
 * - Captions auto-selected by Shopify Markets locale
 */
(() => {
  const SEL = {
    root: '.sigpi',
    teaser: 'video[data-role="teaser"]',
    full: 'video[data-role="full"]',
    modal: '[data-role="modal"]',
    open: '[data-action="open-modal"]',
    close: '[data-action="close-modal"]',
    tapPlay: '[data-action="tap-play"]',
    controls: '[data-role="controls"]'
  };

  const getLocaleCandidates = (localeRaw) => {
    const raw = (localeRaw || '').toString().trim();
    if (!raw) return ['en'];

    const lowered = raw.replace('_', '-').toLowerCase();
    const parts = lowered.split('-').filter(Boolean);
    const primary = parts[0] || 'en';
    const full = parts.length >= 2 ? `${primary}-${parts[1]}` : primary;

    const candidates = [];
    candidates.push(full);
    if (!candidates.includes(primary)) candidates.push(primary);
    if (!candidates.includes('en')) candidates.push('en');
    return candidates;
  };

  const applySubtitlePreference = (videoEl, localeCandidates) => {
    if (!videoEl) return;

    const tracks = Array.from(videoEl.querySelectorAll('track[kind="subtitles"]'));
    if (!tracks.length) return;

    // Reset
    tracks.forEach((t) => {
      try { t.default = false; } catch (_) {}
      try { t.mode = 'disabled'; } catch (_) {}
      try { t.track.mode = 'disabled'; } catch (_) {}
    });

    const match =
      tracks.find((t) => localeCandidates.includes(((t.getAttribute('srclang') || '')).toLowerCase())) ||
      tracks.find((t) => (((t.getAttribute('srclang') || '')).toLowerCase() === 'en'));

    if (match) {
      try { match.default = true; } catch (_) {}
      try { match.mode = 'showing'; } catch (_) {}
      try { match.track.mode = 'showing'; } catch (_) {}
    }
  };

  const lockScroll = (locked) => {
    document.documentElement.classList.toggle('sigpi--scrolllock', locked);
    document.body.classList.toggle('sigpi--scrolllock', locked);
  };

  const getControlsEl = (root, target) => {
    return root.querySelector(`${SEL.controls}[data-target="${target}"]`);
  };

  const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

  const getRangeEl = (controlsEl) => controlsEl?.querySelector?.('input[data-action="volume"]');

  const setControlsState = (controlsEl, videoEl) => {
    if (!controlsEl || !videoEl) return;

    controlsEl.dataset.playing = videoEl.paused ? 'false' : 'true';
    controlsEl.dataset.muted = videoEl.muted ? 'true' : 'false';

    const range = getRangeEl(controlsEl);
    if (range) {
      const value = Math.round(clamp((videoEl.muted ? 0 : (videoEl.volume || 0)) * 100, 0, 100));
      if (Number(range.value) !== value) range.value = String(value);
    }
  };

  const safePlay = (videoEl) => {
    if (!videoEl) return;
    const p = videoEl.play?.();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  const safePause = (videoEl) => {
    if (!videoEl) return;
    try { videoEl.pause(); } catch (_) {}
  };

  const stopVideo = (videoEl) => {
    if (!videoEl) return;
    try { videoEl.pause(); } catch (_) {}
    try { videoEl.currentTime = 0; } catch (_) {}
  };

  const setVolume = (videoEl, value01) => {
    if (!videoEl) return;

    const v = clamp(Number(value01) || 0, 0, 1);
    try { videoEl.volume = v; } catch (_) {}
    try { videoEl.muted = (v <= 0); } catch (_) {}
  };

  const ensureAudibleDefaults = (videoEl, controlsEl, { defaultVolume = 0.75 } = {}) => {
    if (!videoEl) return;

    // If currently muted or volume == 0, give a gentle, safe default volume.
    const currentVol = Number(videoEl.volume || 0);
    if (videoEl.muted || currentVol <= 0.001) {
      try { videoEl.muted = false; } catch (_) {}
      try { videoEl.volume = defaultVolume; } catch (_) {}
    } else {
      try { videoEl.muted = false; } catch (_) {}
    }
    setControlsState(controlsEl, videoEl);
  };

  const bindControls = (root, target, videoEl) => {
    const controlsEl = getControlsEl(root, target);
    if (!controlsEl || !videoEl) return;

    // Initial sync
    setControlsState(controlsEl, videoEl);

    // Click actions (buttons)
    controlsEl.addEventListener('click', (e) => {
      const btn = e.target.closest?.('[data-action]');
      if (!btn) return;

      const action = btn.getAttribute('data-action');
      if (!action) return;

      if (action === 'toggle-play') {
        if (videoEl.paused) {
          // If teaser: if user starts play from controls, show controls & allow sound (but keep volume behavior)
          if (target === 'teaser') root.classList.add('sigpi--userPlaying');
          safePlay(videoEl);
        } else {
          safePause(videoEl);
        }
        setControlsState(controlsEl, videoEl);
        return;
      }

      if (action === 'stop') {
        stopVideo(videoEl);

        // For teaser: revert to silent “preview” mode + show Tap-to-play again
        if (target === 'teaser') {
          try { videoEl.muted = true; } catch (_) {}
          setVolume(videoEl, 0);
          root.classList.remove('sigpi--userPlaying');
        }

        setControlsState(controlsEl, videoEl);
        return;
      }

      if (action === 'toggle-mute') {
        const willMute = !videoEl.muted ? true : false;
        try { videoEl.muted = willMute; } catch (_) {}

        // If unmuting but volume is 0, apply safe default volume.
        if (!willMute) {
          ensureAudibleDefaults(videoEl, controlsEl, { defaultVolume: 0.75 });
          if (target === 'teaser') root.classList.add('sigpi--userPlaying');
        } else {
          // If muted -> keep slider at 0
          setControlsState(controlsEl, videoEl);
        }
        return;
      }
    });

    // Volume range input
    const range = getRangeEl(controlsEl);
    if (range) {
      range.addEventListener('input', () => {
        const val = clamp((Number(range.value) || 0) / 100, 0, 1);

        // Setting volume should also toggle muted state naturally
        setVolume(videoEl, val);

        // Teaser: once user touches controls, keep them visible (mobile-friendly)
        if (target === 'teaser') {
          root.classList.add('sigpi--userPlaying');
        }

        setControlsState(controlsEl, videoEl);
      });
    }

    // Keep UI synced when play/pause/mute changes elsewhere
    videoEl.addEventListener('play', () => setControlsState(controlsEl, videoEl));
    videoEl.addEventListener('pause', () => setControlsState(controlsEl, videoEl));
    videoEl.addEventListener('volumechange', () => setControlsState(controlsEl, videoEl));
  };

  const openModal = (root) => {
    const modal = root.querySelector(SEL.modal);
    if (!modal) return;

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    root.classList.add('sigpi--modalOpen');
    lockScroll(true);

    // Always silence the teaser when opening modal (no background audio).
    const teaser = root.querySelector(SEL.teaser);
    if (teaser) {
      try { teaser.muted = true; } catch (_) {}
      try { teaser.volume = 0; } catch (_) {}
      safePause(teaser);
      root.classList.remove('sigpi--userPlaying');
      setControlsState(getControlsEl(root, 'teaser'), teaser);
    }

    const locale = root.dataset.locale || 'en';
    const candidates = getLocaleCandidates(locale);

   const full = root.querySelector(SEL.full);
if (full) {
  applySubtitlePreference(full, candidates);

  /* ✅ 1) 클릭 1번에 바로 재생되게 준비 */
  try { full.preload = 'auto'; } catch (_) {}
  try { full.currentTime = 0; } catch (_) {}
  try { full.muted = false; full.volume = 0.8; } catch (_) {}
  try { full.load?.(); } catch (_) {}

  /* ✅ 2) 모달이 열린 뒤 레이아웃 확정(첫 오픈 작아짐/두번 클릭 문제 예방) */
  try { void modal.offsetHeight; } catch (_) {}

  /* ✅ 3) 자동 재생 */
  safePlay(full);

  /* ✅ 4) 너무 빠른 play로 막혔을 때, 재생 가능 시점에 1회 재시도 */
  full.addEventListener('canplay', () => {
    if (root.classList.contains('sigpi--modalOpen') && full.paused) safePlay(full);
  }, { once: true });

  setControlsState(getControlsEl(root, 'full'), full);
}


    // Focus close button
    const closeBtn = modal.querySelector('button[data-action="close-modal"]');
    if (closeBtn) {
      try { closeBtn.focus({ preventScroll: true }); } catch (_) {}
    }
  };

  const closeModal = (root) => {
    const modal = root.querySelector(SEL.modal);
    if (!modal) return;

    const full = root.querySelector(SEL.full);
    if (full) {
      stopVideo(full);
      setControlsState(getControlsEl(root, 'full'), full);
    }

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    root.classList.remove('sigpi--modalOpen');
    lockScroll(false);

    // Resume teaser silent autoplay preview
    const teaser = root.querySelector(SEL.teaser);
    if (teaser) {
      try { teaser.muted = true; } catch (_) {}
      try { teaser.volume = 0; } catch (_) {}
      safePlay(teaser);
      root.classList.remove('sigpi--userPlaying');
      setControlsState(getControlsEl(root, 'teaser'), teaser);
    }
  };

  const bindSection = (root) => {
    if (!root || root.dataset.sigpiBound === 'true') return;
    root.dataset.sigpiBound = 'true';

    const locale = root.dataset.locale || 'en';
    const candidates = getLocaleCandidates(locale);

    // Teaser init: silent autoplay preview (muted + volume 0)
    const teaser = root.querySelector(SEL.teaser);
    if (teaser) {
      applySubtitlePreference(teaser, candidates);

      // Ensure silent preview mode
      try { teaser.muted = true; } catch (_) {}
      try { teaser.volume = 0; } catch (_) {}

      safePlay(teaser);
      setControlsState(getControlsEl(root, 'teaser'), teaser);

      // Bind custom teaser controls
      bindControls(root, 'teaser', teaser);
    }

    // Full film controls (inside modal)
    const full = root.querySelector(SEL.full);
    if (full) {
      // Default volume (not muted)
      try { full.muted = false; } catch (_) {}
      try { full.volume = 0.8; } catch (_) {}

      setControlsState(getControlsEl(root, 'full'), full);
      bindControls(root, 'full', full);
    }

    // Open modal
    const openBtn = root.querySelector(SEL.open);
    if (openBtn) {
      openBtn.addEventListener('click', (e) => {
        e.preventDefault();
        openModal(root);
      });
    }

    // Tap-to-play (enables sound for teaser)
    const tapBtn = root.querySelector(SEL.tapPlay);
    if (tapBtn && teaser) {
      tapBtn.addEventListener('click', () => {
        ensureAudibleDefaults(teaser, getControlsEl(root, 'teaser'), { defaultVolume: 0.75 });
        safePlay(teaser);
        root.classList.add('sigpi--userPlaying');
        setControlsState(getControlsEl(root, 'teaser'), teaser);
      });
    }

    // Close modal (X, backdrop etc.) — supports clicking inner span via closest()
    const modal = root.querySelector(SEL.modal);
    if (modal) {
      modal.addEventListener('click', (e) => {
        const closeTarget = e.target.closest?.(SEL.close);
        if (closeTarget) {
          e.preventDefault();
          closeModal(root);
        }
      });
    }
  };

  const init = () => {
    document.querySelectorAll(SEL.root).forEach(bindSection);

    // Shopify theme editor support
    document.addEventListener('shopify:section:load', (e) => {
      const container = e.target;
      if (!container) return;
      container.querySelectorAll?.(SEL.root).forEach(bindSection);
    });

    // Global ESC close (only once)
    if (!window.__sigpiEscBound) {
      window.__sigpiEscBound = true;
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;

        document.querySelectorAll(SEL.root).forEach((root) => {
          const modal = root.querySelector(SEL.modal);
          if (!modal || modal.hidden) return;
          closeModal(root);
        });
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
